import { useEffect, useRef, useState } from "react";
import { ReactComponent as FilterIcon } from "../../../assets/icons/filter-2.svg";
import { ReactComponent as CarouselLeftIcon } from "../../../assets/icons/chevron-down.svg";
import { Checkbox } from "../../../components/Checkbox";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { Button } from "../../../components/Button";



export default function FilterView({ cohorts, onFilter }: { cohorts: any[], onFilter: (form: any) => void }) {
    const [isOpen, setIsOpen] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);
    const [form, setForm] = useState({
        startDate: '',
        endDate: '',
        days: '7',
        quarter: false,
        hasComplete: false,
        hasIncomplete: false,
        cohort: '',
        sortBy: '',
        sortDirection: 'ASC',
    });

    const handleFilter = () => {
        onFilter(form);
        setIsOpen(false);
    }

    const handleReset = () => {
        setForm({
            startDate: '',
            endDate: '',
            days: '7',
            quarter: false,
            hasComplete: false,
            hasIncomplete: false,
            cohort: '',
            sortBy: '',
            sortDirection: 'ASC',
        });
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    return (
        <div className="relative z-[1]">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-[120px] h-[45px] bg-white rounded-[6px] px-3 py-3 flex items-center gap-2"
            >
                <FilterIcon className={`w-5 h-5 `} />
                <div className="text-center font-normal text-base">
                    Filters
                </div>
                <CarouselLeftIcon className={`w-5 h-5 [&_path]:stroke-[#1A1A1A] `} />

            </button>
            {isOpen && <div
                ref={popupRef}
                className="rounded-[6px] w-[400px] bg-white shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] absolute top-[110%] left-0">
                <div className="flex flex-col gap-4 py-6">
                    <div className="flex justify-between items-center px-5">
                        <span className="text-sm font-medium">Filters</span>
                        <button className="underline text-sm font-semibold text-[#1A1A1A]" onClick={handleReset}>Reset</button>
                    </div>
                    <div className="flex flex-col gap-6 pl-5 pr-2">
                        <div className="flex flex-col gap-2">
                            <span className="text-[15px] font-semibold">Time-Based</span>
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center">
                                    <div className="text-sm font-medium mr-2.5">Date Range:</div>

                                    <DatePicker
                                        className="border border-[#E9E9E9] text-[#A7A7A7] text-xs bg-[#F3F3F3] w-[7.5rem] rounded !pr-1.5 py-1 !outline-none !pl-8"
                                        selected={form.startDate ? dayjs(form.startDate).toDate() : null}
                                        onChange={(date) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                startDate: date ? dayjs(date).format('YYYY-MM-DD') : '',
                                                endDate: '',
                                                days: '',
                                                quarter: false,
                                            }))
                                        }}
                                        dateFormat="MMM dd, yyyy"
                                        icon={<CalendarIcon className="[&_path]:stroke-[#A7A7A7] !w-5 !h-5 !px-[7px] !pt-[5px] !pb-[4px]" />}
                                        showIcon={true}
                                    />
                                    <div className="text-sm mx-2.5">to</div>
                                    <DatePicker
                                        className="border border-[#E9E9E9] text-[#A7A7A7] text-xs bg-[#F3F3F3] w-[7.5rem] rounded px-2 py-1 !outline-none !pl-8"
                                        selected={form.endDate ? dayjs(form.endDate).toDate() : null}
                                        onChange={(date) => setForm((prev) => ({
                                            ...prev,
                                            endDate: date ? dayjs(date).format('YYYY-MM-DD') : '',
                                            startDate: date && form.startDate && dayjs(date).isBefore(form.startDate) ? dayjs(date).format('YYYY-MM-DD') : form.startDate,
                                            days: '',
                                            quarter: false,
                                        }))}
                                        dateFormat="MMM dd, yyyy"
                                        icon={<CalendarIcon className="[&_path]:stroke-[#A7A7A7] !w-5 !h-5 !px-[7px] !pt-[5px] !pb-[4px]" />}
                                        showIcon={true}
                                    />
                                </div>
                                <Checkbox
                                    checked={form.days === '7'}
                                    onChange={() => {
                                        setForm((prev) => ({
                                            ...prev,
                                            days: '7',
                                            startDate: '',
                                            endDate: '',
                                            quarter: false,
                                        }));
                                    }}
                                    label="Last 7 days"
                                    id="7days"
                                    inputClassName="!accent-[#DB622B]"
                                />

                                <Checkbox
                                    checked={form.days === '30'}
                                    onChange={() => {
                                        setForm((prev) => ({
                                            ...prev,
                                            days: '30',
                                            startDate: '',
                                            endDate: '',
                                            quarter: false,
                                        }));
                                    }}
                                    label="Last 30 days"
                                    id="30days"
                                    inputClassName="!accent-[#DB622B]"
                                />

                                <Checkbox
                                    checked={form.quarter}
                                    onChange={() => {
                                        setForm((prev) => ({
                                            ...prev,
                                            quarter: !prev.quarter,
                                            days: '',
                                            startDate: '',
                                            endDate: '',
                                        }));
                                    }}
                                    label="This Quarter"
                                    id="quarter"
                                    inputClassName="!accent-[#DB622B]"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <span className="text-[15px] font-semibold">Engagement</span>
                            <div className="flex flex-col gap-2">

                                <Checkbox
                                    checked={form.hasComplete}
                                    onChange={() => {
                                        setForm((prev) => ({
                                            ...prev,
                                            hasComplete: !prev.hasComplete,
                                        }));
                                    }}
                                    label="Completed Sessions"
                                    id="complete"
                                    inputClassName="!accent-[#DB622B]"
                                />

                                <Checkbox
                                    checked={form.hasIncomplete}
                                    onChange={() => {
                                        setForm((prev) => ({
                                            ...prev,
                                            hasIncomplete: !prev.hasIncomplete,
                                        }));
                                    }}
                                    label="Incomplete Sessions"
                                    id="incomplete"
                                    inputClassName="!accent-[#DB622B]"
                                />
                            </div>
                        </div>
                        {cohorts.length > 0 ? <div className="flex flex-col gap-2">
                            <span className="text-[15px] font-semibold">Cohort</span>
                            <div className="flex flex-col gap-2">
                                {
                                    cohorts.map((cohort) => (
                                        <Checkbox
                                            checked={form.cohort === cohort.key}
                                            onChange={() => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    cohort: cohort.key,
                                                }));
                                            }}
                                            label={cohort.name}
                                            id={cohort.key}
                                            inputClassName="!accent-[#DB622B]"
                                        />
                                    ))}
                            </div>
                        </div> : null}
                        <div className="flex justify-end">
                            <Button variant="primary" onClick={handleFilter} className="!py-2 !px-8 !rounded-full">Filter</Button>
                        </div>
                    </div>
                </div>
            </div>}
        </div>
    )
}